export default {
  id: "timesheet-billing",
  title: "Timesheet Billing Report",
  section: "Billing & Reports",
  order: 50,
  keywords: "timesheet billing state upload SEI units rounding line by line DSPD csv rebill",
  body: `
## What it is

Some DSPD service codes are billed **line by line** — one row per shift —
through the state's timesheet upload, instead of one consolidated monthly
total. SEI was the first code converted (July 2026), and the state may
convert more. The Timesheet Billing Report turns a month of approved shifts
into the state's exact upload file, so billing is: run the report, fix any
warnings, download, upload to the state. No spreadsheet reformatting.

## How it works

The report takes every **Approved** shift in your date range for the codes
you select and builds one row per shift in the state's column layout:

| Column | Where it comes from |
|--------|---------------------|
| line_number | Counted 1, 2, 3... down the file (rows are grouped by client, then date) |
| provider_approver_email | The email you type when running the report |
| consumer_name / consumer_pid | The client's name and DSPD PID (leading zeros kept) |
| service_code | The shift's service code |
| rate | The client's authorization rate; if the auth has none, the service code's rate |
| unit_type | Always Q (quarter-hour units) |
| service_start_date / end_date | The clock-in and clock-out dates, like 7/8/2026 — always the same day on a line (see overnight shifts below) |
| units | Calculated from the clock times — see the rounding rule below |
| service_start_time / end_time | Clock times in military format without a leading zero: 9:02, 13:22 |
| employee_id | The caregiver's Position ID, zero-padded to six digits (10507 becomes 010507) |

### The unit rounding rule (audit compliance)

**This is not the rounding used for VR billing.** For timesheet billing:

> Take the total minutes worked (clock-out minus clock-in) and divide by 15.
> A remainder of **8 or more minutes rounds up** to the next unit; **7 or
> less rounds down**.

Worked example: 9:02 AM to 1:22 PM is 260 minutes. 260 ÷ 15 = 17 units with
5 minutes left over. 5 is 7 or less, so the shift bills **17 units**. A
shift of 189 minutes (12 units + 9 minutes) rounds **up** to 13. A shift
shorter than 8 minutes bills 0 units.

The always-round-up rule used by the Monthly Billing Report would have said
18 for that first example — that's why the two reports show different unit
counts for the same shift. For timesheet-billed codes, the timesheet
number is the correct one.

### Overnight shifts split into one line per day

Every line must stay within one calendar day (**local time**, not UTC), so
a shift that crosses midnight automatically becomes **two lines** — one per
day, each with its own units:

> Example: a shift from 9:00 PM to 1:30 AM becomes line one **21:00 →
> 23:59** on the first day (12 units for the 180 minutes up to midnight)
> and line two **0:00 → 1:30** on the second day (6 units for 90 minutes).

The 23:59 on the first line is display only — the minute before midnight
is still counted; no time is lost. The rounding rule applies to each
line's own minutes, and a shift ending exactly at midnight stays one line
(shown ending 23:59).

## Step by step: monthly billing run

1. **Approve the month's shifts first.** Unapproved shifts don't appear.
   The Verification page shows how many clocked-out shifts are still
   unapproved.
2. Go to **Reports** and pick **"Timesheet Billing Report"**.
3. Set the date range to the billing month (Month mode with the arrows, or
   Custom for an odd range).
4. Payer is DSPD by default. In **Service Code**, select the code(s) being
   timesheet-billed — e.g. SEI.
5. Type the **approver email** (remembered from last time).
6. Look at the **yellow warnings box** above the table — every warning is
   something the state would bounce:
   - *Missing clock in/out* — the line is **left out of the file** until the
     shift's times are fixed (edit the shift, then re-run).
   - *Client has no DSPD PID* — the PID cell is blank. Add the PID on the
     client's Details page.
   - *Caregiver has no employee number* — the employee_id cell is blank. Set
     the caregiver's Position ID.
   - *No rate* — set the rate on the client's authorization (preferred) or
     on the service code.
7. Fix the data, re-run (change any filter or reselect the report), and when
   the warnings are gone click **Download CSV**. The file is named with the
   date range and is ready for the state system.

## Step by step: re-billing specific lines

When the state rejects lines or a correction is needed:

1. Run the report filtered to just the affected **client** (and caregiver if
   needed) with a **custom date range** covering only the affected days.
2. Download — you get a small file with just those lines, numbered from 1.

## Which codes use timesheet billing?

| Code | Since |
|------|-------|
| SEI  | July 2026 |

When the state converts another code, nothing needs to be built — select
that code when running the report. (If several codes are converted, you can
select them all and produce one combined file.)
`,
};
